<template>
  <div v-if="model.state.gameValid" class="poll" :class="wrapperClasses">
    <component is="style" type="text/css">{{ model.state.elementStyling }}</component>
    <div v-if="isGameReady" class="poll__game">
      <div class="poll__game-contents">
        <div class="question question-- question-- flow-transition" :class="{ 'flow-transition--ready': isReady }">
          <div class="question__content">
            <img
              v-if="model.state.question.questionImage"
              class="question__image"
              alt=""
              :src="model.state.question.questionImage"
            />
            <div v-if="getVideoFromUrl" class="question__video embed-responsive embed-responsive-16by9">
              <iframe
                class="question__video-iframe embed-responsive-item"
                width="450"
                height="275"
                :src="getVideoFromUrl"
                webkitallowfullscreen
                mozallowfullscreen
                allowfullscreen
              ></iframe>
            </div>
            <!-- nosem -->
            <h3 class="question__wrapper" v-html="model.state.question.question"></h3>
            <div
              class="question__answers"
              :class="[
                { 'animated shake': isError },
                { 'question__answers--images question__answers--has-layout': model.state.layout?.image && isImageType },
                { 'question__answers--images': isImageType },
                {
                  'question__answers--images-headline': model.state.question.displayType === DisplayType.IMAGE_HEADLINE
                },
                imageColumns,

                { 'question__answers--videos row': model.state.question.displayType === DisplayType.VIDEO }
              ]"
            >
              <img
                v-if="
                  (model.state.question.displayType === DisplayType.IMAGE ||
                    model.state.question.displayType === DisplayType.IMAGE_HEADLINE) &&
                  model.state.layout?.image
                "
                class="question__answer-item-image"
                :src="model.state.layout?.image"
                alt=""
              />
              <AnswerItem
                v-for="(answer, index) in answers"
                :key="index"
                :item="answer"
                :settings="model.state.general"
                :question="model.state.question"
                :layout="model.state.layout"
                :question-image-size="imageSize"
                :answer-classes="[
                  answer.paused ? 'question__answer-item--paused' : '',
                  `question__answer-item--rank-${index + 1}`,
                  answerExists(answer) ? 'question__answer-item--selected' : '',
                  model.state.question.displayType === DisplayType.VIDEO
                    ? 'question__video-item col-lg-3 col-md-4 col-md-6'
                    : ''
                ]"
                @onSelect="onSelectAnswer"
              >
                <AnswerResult
                  v-if="showResult && model.state.showVotes === ShowVoteType.GAME"
                  :value="votePercentageMap[answer.id.toString()]"
                  :delay="1000"
                  :duration="1750"
                  :count="answer.votes"
                  :animate="model.state.general.animateResult"
                  :result-type="model.state.general.resultIn"
                ></AnswerResult>
              </AnswerItem>
            </div>
            <div v-if="hasAnswerButton" class="content">
              <div class="content__item content__item--action">
                <p v-if="isError" class="content__item-action-error">{{ model.state.general.selectAnswerBtn }}</p>
                <UIButton :loading="isLoading" :disabled="isError" @click="onSubmit">
                  <span class="content__item-action-text">{{ model.state.general.answerBtn }}</span>
                </UIButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="#app">
      <transition name="fade" :appear="true">
        <div
          v-if="popoverOpen"
          id="pollModal"
          :data-page-id="12"
          :data-disable-default="true"
          :style="{ backgroundColor: model.state.general.overlayBackground, display: 'block' }"
          class="poll-modal modal"
        >
          <div class="poll-modal__dialog modal-dialog" role="document">
            <div class="poll-modal__content modal-content" :style="popupStyling.background">
              <div class="poll-modal__header modal-header">
                <button type="button" class="poll-modal__close close" aria-label="Close" @click="popoverOpen = false">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <RichTextRenderer class="poll-modal__body modal-body" :html="promptMessages.confirm"></RichTextRenderer>
              <div class="poll-modal__footer modal-footer">
                <button
                  type="button"
                  class="poll-modal__action poll-modal__action--yes btn"
                  :style="popupStyling.confirmButton"
                  @click="onPopoverAnswer"
                >
                  {{ promptMessages.yes }}
                </button>
                <button
                  type="button"
                  class="poll-modal__action poll-modal__action--no btn"
                  :style="popupStyling.closeButton"
                  @click="popoverOpen = false"
                >
                  {{ model.state.general.cancelText }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script lang="ts">
import type { CSSProperties, PropType } from 'vue';
import { nextTick } from 'vue';
import { watch } from 'vue';
import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import type { GamePollAnswerState, GamePollModel, GamePollTranslations } from './Model';
import { DisplayType, ResultType, ShowVoteType, VoteType } from '@/src/components/games/poll/Enums';
import AnswerItem from '@/src/components/games/poll/AnswerItem.vue';
import {
  applyReplacementTags,
  generateVimeoUrl,
  generateYouTubeUrl,
  getVimeoIdFromUrl,
  getYouTubeVideoIdFromUrl,
  inIframe,
  stripHtml
} from '@/src/utilities/Utilities';
import useEndGame from '@/src/hooks/useEndGame';
import AnswerResult from '@/src/components/games/poll/AnswerResult.vue';
import { playAudio } from '@/src/utilities/AudioPlayer';
import RichTextRenderer from '@/src/components/RichTextRenderer.vue';
import {
  AnswerBulletsTypes,
  AnswerDesignTypes,
  ImageColumnsTypes
} from '@/src/models/settings/AnswerLayoutSettingsModel';
import useDevice from '@/src/hooks/useDevice';
import type { ClassList } from '@/src/typings/types/types';
import UIButton from '@/src/components/ui/UIButton.vue';
import { VideoFormatType } from '@/src/typings/enums/enums';
import type { MetricData } from '@/src/store/campaign';
import { useCampaignStore } from '@/src/store/campaign';
import type { Distribution } from '@/src/components/games/poll/Utility';
import { getDistribution } from '@/src/components/games/poll/Utility';
import { sendEvent } from '@/src/utilities/iFrameUtils';

type DesignType = 'bullets' | 'radiobuttons' | 'text' | 'custom';
type ListStyleType = 'disc' | 'decimal' | 'letter' | 'none';

interface PopupStyling {
  background: CSSProperties;
  confirmButton: CSSProperties;
  closeButton: CSSProperties;
}

export default defineComponent({
  name: 'GamePoll',
  components: {
    UIButton,
    AnswerItem,
    AnswerResult,
    RichTextRenderer
  },
  props: {
    model: {
      type: Object as PropType<GamePollModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();
    const selectedAnswer = ref<GamePollAnswerState | null>(null);
    const selectedAnswers = ref<GamePollAnswerState[]>([]);
    const isReady = ref(true);
    const isGameReady = ref(true);
    const isError = ref(false);
    const popoverOpen = ref(false);
    const originUrl = ref(window.location.href);

    const showResult = ref(props.model.state.showVotes && !props.model.state.general.showResultAfterSubmit);
    const isLoading = ref(false);

    let gameOver = false;

    const imageSize = reactive({
      width: 0,
      height: 0
    });

    const hasAnswerButton = computed(() => {
      if (
        (props.model.state.layout?.image && props.model.state.general?.showConfirmPopup) ||
        props.model.state.general.voteType === VoteType.BUTTON
      ) {
        return false;
      }

      return props.model.state.showAnswerButton;
    });

    const isImageType = computed(() => {
      return (
        props.model.state.question.displayType === DisplayType.IMAGE ||
        props.model.state.question.displayType === DisplayType.IMAGE_HEADLINE ||
        props.model.state.question.displayType === DisplayType.IMAGE_DESCRIPTION
      );
    });

    const popupStyling = computed<PopupStyling>(() => {
      return {
        background: { backgroundColor: props.model.state.general.popupBackground },
        confirmButton: { backgroundColor: props.model.state.general.confirmBackground },
        closeButton: { backgroundColor: props.model.state.general.closeBackground }
      };
    });

    const wrapperClasses = computed<ClassList>(() => {
      let designType: DesignType | undefined;

      switch (props.model.state.question.answerLayout.state.designType) {
        case AnswerDesignTypes.BULLETS:
          designType = 'bullets';
          break;
        case AnswerDesignTypes.RADIOBUTTONS:
          designType = 'radiobuttons';
          break;
        case AnswerDesignTypes.CUSTOM:
          designType = 'custom';
          break;
        default:
          designType = 'text';
          break;
      }

      let listStyleType: ListStyleType | undefined;

      switch (props.model.state.question.answerLayout.state.listStyle) {
        case AnswerBulletsTypes.DISC:
          listStyleType = 'disc';
          break;
        case AnswerBulletsTypes.DECIMAL:
          listStyleType = 'decimal';
          break;
        case AnswerBulletsTypes.LETTER:
          listStyleType = 'letter';
          break;
        default:
          listStyleType = 'none';
          break;
      }

      return {
        [`poll--${designType}`]: !!designType,
        [`poll--${designType}-${listStyleType}`]: designType === 'bullets' && !!listStyleType,
        [`poll--answers-align-${props.model.state.question.answerLayout.state.alignment}`]:
          !!props.model.state.question.answerLayout.state.alignment,
        'poll--has-active-state':
          !!props.model.state.question.answerLayout.state.activeAnswer?.backgroundColor ||
          !!props.model.state.question.answerLayout.state.activeAnswer?.textColor
      };
    });

    const imageColumns = computed(() => {
      let imageColumns: string | undefined;

      if (useDevice().isMobile) {
        switch (props.model.state.question.answerLayout.state.imageColumns) {
          case ImageColumnsTypes.ONE_COLUMN:
            imageColumns = 'question__answers--images-1';
            break;

          case ImageColumnsTypes.TWO_COLUMNS:
            imageColumns = 'question__answers--images-2';
            break;
        }
      } else {
        switch (props.model.state.question.answerLayout.state.imageColumns) {
          case ImageColumnsTypes.ONE_COLUMN:
            imageColumns = 'question__answers--images-desktop-1';
            break;

          case ImageColumnsTypes.TWO_COLUMNS:
            imageColumns = 'question__answers--images-desktop-2';
            break;

          case ImageColumnsTypes.THREE_COLUMNS:
            imageColumns = 'question__answers--images-desktop-3';
            break;
        }
      }

      return imageColumns;
    });

    const answers = computed(() => {
      return props.model.state.answers;
    });

    const promptMessages = computed<GamePollTranslations>(() => {
      return {
        confirm: applyReplacementTags(stripHtml(props.model.state.general.popupText)),
        yes: applyReplacementTags(props.model.state.general.confirmText)
      };
    });

    const answerExists = (answer: GamePollAnswerState) => {
      return selectedAnswers.value.some((value) => {
        return value.id === answer.id;
      });
    };

    const onMultipleAnswers = (answer: GamePollAnswerState) => {
      // if button is not enabled, you are not allowed to select multiple
      // Answer exists - remove it.
      if (answerExists(answer)) {
        for (const key in selectedAnswers.value) {
          if (selectedAnswers.value[Number(key)] === answer) {
            selectedAnswers.value.splice(Number(key), 1);
          }
        }
      } else if (
        // max amount of answers
        props.model.state.question.multiAnswerOptions &&
        selectedAnswers.value.length < props.model.state.question.multiAnswerOptions
      ) {
        selectedAnswers.value.push(answer);
      } else if (!props.model.state.question.multiAnswerOptions) {
        // infinite answers
        selectedAnswers.value.push(answer);
      }
    };

    let selectedClick = 0;

    const onSelectAnswer = (answer: GamePollAnswerState, hasConfirm: boolean) => {
      if (props.model.state.advanced.sound.enabled && props.model.state.advanced.sound.src) {
        playAudio(props.model.state.advanced.sound.src);
      }

      if (answer.id === selectedAnswer.value?.id) {
        selectedAnswer.value = null;
      }

      selectedAnswer.value = answer;

      // handle single select or multi - if the user click on another image. do nothing.
      campaignStore.addReplacementTags(getAnswerReplacementTags(answer));

      if (hasConfirm) {
        popoverOpen.value = true;
        return;
      }

      if (props.model.state.question.multiAnswer && !hasConfirm) {
        selectedClick++;
        onMultipleAnswers(answer);
      } else if (!props.model.state.question.multiAnswer) {
        selectedAnswers.value = [];
        selectedClick++;
        // if answer has popover let the popover handle submit

        if (props.model.state.question.displayType === DisplayType.VIDEO) {
          selectedAnswers.value.push(answer);
          onSubmit();
          return;
        }

        // instant submit + counter to prevent edge case submits
        if (!hasAnswerButton.value && !hasConfirm && selectedClick === 1 && !props.model.state.question.multiAnswer) {
          selectedAnswers.value.push(answer);
          onSubmit();
        } else {
          selectedAnswers.value.push(answer);
        }
      }
    };

    const getFormattedAnswerChoice = (value: string) => {
      const isImage = value.includes('/files');
      const youtubeVideoId = getYouTubeVideoIdFromUrl(value);
      const vimeoVideoId = getVimeoIdFromUrl(value);

      if (youtubeVideoId) {
        return `<iframe
        width="450"
        height="275"
        webkitallowfullscreen
        mozallowfullscreen
        allowfullscreen
        src="${generateYouTubeUrl(youtubeVideoId)}"></iframe>`;
      }

      if (vimeoVideoId) {
        return `<iframe
        width="450"
        height="275"
        webkitallowfullscreen
        mozallowfullscreen
        allowfullscreen
        src="${generateVimeoUrl(vimeoVideoId)}"></iframe>`;
      }

      if (isImage) {
        return `<img src="${value}" style="max-width: 60%;" />`;
      }

      return value;
    };

    const getAnswerReplacementTags = (answer: GamePollAnswerState) => {
      const isImage = answer.value.includes('/files');
      // adds users own input
      const totalVotes = props.model.state.totalVotes + 1;

      return {
        ...(answer.value && {
          choice: getFormattedAnswerChoice(answer.value)
        }),
        ...(answer.value &&
          isImage && {
            image: `<img src="${answer.value}" style="max-width: 60%;" />`
          }),
        ...(answer.headline && { choice_label: answer.headline }),
        ...(answer.description && { choice_text: answer.description }),
        ...(answer.buttonLabel && { button_label: answer.buttonLabel }),
        ...(answer.headline2 && { choice_headline: answer.headline2 }),
        ...{ total_votes: totalVotes },
        ...{ total_votes_summary: getSummaryReplacementTags() }
      };
    };

    const votePercentageMap = computed(() => {
      const map: { [key: string]: number } = {};
      props.model.state.answers?.forEach((answer) => {
        const distributionItem = voteDistribution.value.find((item) => item.id === answer.id.toString());
        map[answer.id.toString()] =
          distributionItem && distributionItem.votes_percentage ? parseFloat(distributionItem.votes_percentage) : 0;
      });
      return map;
    });

    const voteDistribution = computed(() => {
      const votes: Distribution[] =
        props.model.state.answers?.map((answer) => ({
          id: answer.id.toString(),
          votes: answer.votes
        })) ?? [];

      return getDistribution(votes);
    });

    const getSummaryReplacementTags = () => {
      let summary = '<ul class="poll__summary">';

      // Build the summary using the distribution
      if (props.model.state.answers) {
        props.model.state.answers.forEach((answer) => {
          const answerValue = getFormattedAnswerChoice(answer.value);

          summary += `<li class="poll__summary-item"><label class="poll__summary-label">${answerValue}</label><span class="poll-summary-value">${
            votePercentageMap.value[answer.id.toString()] ? votePercentageMap.value[answer.id.toString()] : 0
          }</span></li>`;
        });
      }

      return summary;
    };

    const getReplacementTags = () => {
      let votes = props.model.state.totalVotes;
      let userChoice = '';
      let userChoiceHeadline = '';
      let choiceText = ''; // Initialize the choiceText variable

      selectedAnswers.value.forEach((answer, index) => {
        votes++;
        // adds a (,) and space if its not the last item in the answers
        userChoice += `${
          getFormattedAnswerChoice(answer.value) +
          (index === selectedAnswers.value.length - 1 || isImageType.value ? '' : ',&nbsp; ')
        }`;
        userChoiceHeadline += `${
          (answer.headline || answer.headline2 || '') + (index === selectedAnswers.value.length - 1 ? '' : ',&nbsp; ')
        }`;

        // Concatenates answer.description with a comma and a non-breaking space if it's not the last item
        if (answer.description) {
          choiceText += `${answer.description + (index === selectedAnswers.value.length - 1 ? '' : '')}`;
        }
      });

      // Constructs the replacement tags object
      const replacementTags = {
        ...(userChoice && { choice: userChoice }),
        ...(userChoiceHeadline && { choice_label: userChoiceHeadline }),
        ...(userChoiceHeadline && { choice_headline: userChoiceHeadline }),
        ...(votes && { total_votes: votes }),
        ...(userChoice && isImageType.value && { image: userChoice }),
        ...(choiceText && { choice_text: choiceText }), // Adds the choice_text if it exists
        total_votes_summary: getSummaryReplacementTags()
      };

      return replacementTags;
    };

    const getMetrics = () => {
      const metrics: MetricData = {};

      if (props.model.state.question.multiAnswer) {
        selectedAnswers.value.forEach((answer) => {
          Object.assign(metrics, { [`answer_${answer.id}`]: 1 });
        });
      } else {
        Object.assign(metrics, { [`answer`]: selectedAnswer.value?.id });
      }

      return metrics;
    };

    const onSubmit = async () => {
      if (gameOver) return;
      const startTime = new Date().getTime();
      // no answer selected exit function with error message
      if (selectedAnswers.value.length === 0) {
        isError.value = true;

        window.setTimeout(() => {
          isError.value = false;
        }, 1200);

        return;
      }

      isLoading.value = true;

      const metrics = getMetrics();
      const replacementTags = getReplacementTags();

      gameOver = true;

      if (props.model.state.general.showResultAfterSubmit) {
        showResult.value = true;

        window.setTimeout(
          () => {
            isLoading.value = false;
            useEndGame(true);
          },
          props.model.state.general.animateResult ? 4000 : 2000
        );
      } else {
        window.setTimeout(
          () => {
            isLoading.value = false;
            useEndGame(true);
          },
          Math.max(0, 625 - (new Date().getTime() - startTime))
        );
      }

      if (metrics) {
        campaignStore.metricData = metrics;
      }

      if (replacementTags) {
        campaignStore.addReplacementTags(replacementTags);
      }
    };

    const onPopoverAnswer = async () => {
      selectedAnswers.value = [];
      if (selectedAnswer.value) {
        selectedAnswers.value.push(selectedAnswer.value);
      }
      await onSubmit();
      popoverOpen.value = false;
    };

    const getVideoFromUrl = computed(() => {
      if (props.model.state.question.questionVideo?.includes(VideoFormatType.YOUTUBE)) {
        const youtubeVideoId = getYouTubeVideoIdFromUrl(props.model.state.question.questionVideo);
        if (youtubeVideoId) {
          return generateYouTubeUrl(youtubeVideoId);
        }
      }

      if (props.model.state.question.questionVideo?.includes(VideoFormatType.VIMEO)) {
        const vimeoVideoId = getVimeoIdFromUrl(props.model.state.question.questionVideo);
        if (vimeoVideoId) {
          return generateVimeoUrl(vimeoVideoId);
        }
      }
      return undefined;
    });

    const onPreloadImage = async (imageSrc: string): Promise<HTMLImageElement> => {
      return new Promise<HTMLImageElement>((resolve, reject): HTMLImageElement => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = imageSrc;

        return image;
      });
    };

    let preloadPromiseResolve: () => void | undefined;

    const preloadPromise = new Promise<void>((resolve) => {
      preloadPromiseResolve = resolve;
    });

    watch(popoverOpen, async (newVal) => {
      if (newVal) {
        await nextTick();
        document.body.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (inIframe()) {
          sendEvent('scroll-to-top');
        }
      }
    });

    watch(
      () => useDevice().device,
      () => {
        if (props.model.state.layout?.image) {
          onPreloadImage(props.model.state.layout.image)
            .then((image) => {
              imageSize.width = image.naturalWidth;
              imageSize.height = image.naturalHeight;
              preloadPromiseResolve();
            })
            // resolve even if we can't find image
            .catch(() => {
              preloadPromiseResolve();
            });
        } else {
          preloadPromiseResolve();
        }
      }
    );

    onMounted(async () => {
      if (props.model.state.layout?.image) {
        onPreloadImage(props.model.state.layout.image)
          .then((image) => {
            imageSize.width = image.naturalWidth;
            imageSize.height = image.naturalHeight;
            preloadPromiseResolve();
          })
          // resolve even if we can't find image
          .catch(() => {
            preloadPromiseResolve();
          });
      } else {
        preloadPromiseResolve();
      }
    });

    return {
      isLoading,
      answers,
      imageSize,
      originUrl,
      promptMessages,
      selectedAnswer,
      selectedAnswers,
      answerExists,
      isGameReady,
      isReady,
      popoverOpen,
      isImageType,
      isError,
      DisplayType,
      onSelectAnswer,
      onSubmit,
      onPopoverAnswer,
      getVideoFromUrl,
      showResult,
      ResultType,
      ShowVoteType,
      wrapperClasses,
      imageColumns,
      votePercentageMap,
      popupStyling,
      hasAnswerButton,
      onBeforeEnter: async () => {
        await preloadPromise;
      }
    };
  }
});
</script>

<style>
.poll
  .question__answers
  .question__answer-item.question__video-item.question__answer-item--paused
  .question__answer-item-inner,
.poll
  .question__answers
  .question__answer-item.question__video-item.question__answer-item--paused
  .question__answer-item-text,
.poll .question__answers.question__answers--images.question__answers--has-layout .question__answer-item--paused {
  opacity: 0.5;
}

.poll
  .question__answers
  .question__answer-item.question__answer-item--paused.question__image
  .question__answer-item-image,
.poll
  .question__answers
  .question__answer-item.question__answer-item--paused.question__image:hover
  .question__answer-item-description,
.poll
  .question__answers
  .question__answer-item.question__answer-item--paused.question__image:hover
  .question__answer-item-image,
.poll .question__answers .question__answer-item.question__answer-item--paused.question__image:hover h4,
.poll .question__answers .question__answer-item.question__answer-item--paused:hover > .question__answer-item-wrapper,
.poll .question__answers .question__answer-item.question__answer-item--paused > .question__answer-item-wrapper {
  opacity: 0.2;
}

.poll__summary-item:after {
  content: '';
  display: block;
  font-size: 0;
  line-height: 0;
  letter-spacing: 0;
  clear: both;
}

.poll__summary-label {
  display: block;
  width: 13.5rem;
  float: left;
}

.poll__summary-value {
  display: block;
  float: left;
}

.poll__game-contents {
  position: relative;
}

.poll .question .question__wrapper,
.poll .question__answers,
.poll .question__link {
  padding: 0 1rem;
}

.poll .question .question__wrapper {
  font-size: 3.2rem;
  margin-left: -0.2rem;
  margin-bottom: 1.5rem;
  margin-top: -0.5rem;
}

.poll .question__content,
.poll .content {
  text-align: center;
}

.poll .question .question__content .question__image {
  margin: 0 auto 3rem;
  max-width: 30rem;
  backface-visibility: hidden;
  transition: all 150ms linear !important;
  img {
    transition: all 150ms linear !important;
  }
}

.poll .question__video {
  display: block;
  width: 45rem;
  max-width: 100%;
  margin: 0 auto 3rem auto;
  backface-visibility: hidden;
}

.poll .question__answers {
  font-size: 2.4rem;
  animation-duration: 0.8s;
}

.poll .question__answers .question__answer-item {
  transform: scale(1);
  transition:
    transform 0.15s ease-out,
    opacity 0.15s linear;

  margin-bottom: 0.5rem;
  position: relative;
  text-align: center;
  font-weight: normal;
  padding: 0.3rem 1rem;
  line-height: 1;
}

.poll .question__answers .question__answer-item.question__image {
  transition: opacity 0.15s linear;
}

.poll .question__answers .question__answer-item > .question__answer-item-wrapper {
  display: inline-block;
  cursor: pointer;
  opacity: 0.5;
  position: relative;
  line-height: 1.2;
  transition: opacity 0.15s linear;
}

.poll.poll--has-active-state .question__answers .question__answer-item .question__answer-item-wrapper {
  opacity: 1;
}

.poll .question__answers.question__answers--images.question__answers--has-layout {
  display: inline-block;
  width: auto;
  height: auto;
  position: relative;
  padding: 0 !important;
}

.poll .question__answers.question__answers--images.question__answers--has-layout > .question__answer-item-image {
  display: block;
  max-width: 100%;
  margin: 0;
}

.poll .question__answers.question__answers--images.question__answers--has-layout .question__answer-item {
  position: absolute;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;
  cursor: pointer;
}

.poll
  .question__answers.question__answers--images.question__answers--has-layout.question__answers--images-headline
  .question__answer-item {
  background-image: none !important;
}

.poll.poll--answers-align-left .question__answers .question__answer-item {
  text-align: left;
}

.poll.poll--answers-align-center .question__answers .question__answer-item {
  text-align: center;
}

.poll.poll--answers-align-right .question__answers .question__answer-item {
  text-align: right;
}

.poll .question__answers .question__answer-item .question__answer-item-radio {
  position: relative;
  box-sizing: border-box;
  display: inline-block;
  border-radius: 50%;
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  margin: -0.4rem 1rem 0 -3rem;
  text-align: center;
  background: #fafafa;
  vertical-align: middle;
}

.poll .question__answers .question__answer-item .question__answer-item-radio > .question__answer-item-radio-icon {
  content: '';
  box-sizing: border-box;
  position: absolute;
  top: 0.32rem;
  left: 0.32rem;
  width: 1.36rem;
  height: 1.36rem;
  border-radius: 50%;
  transform: scale(0);
  background: #292828;
  opacity: 0;
  transition:
    opacity 150ms linear,
    transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

.poll
  .question__answers
  .question__answer-item.question__answer-item--selected
  .question__answer-item-radio
  > .question__answer-item-radio-icon {
  transform: scale(1);
  opacity: 1;
}

.poll
  .question__answers
  .question__answer-item:not(.question__answer-item--selected)
  .question__answer-item-checkmark::before {
  color: transparent;
}

.poll .question__answer-item .question__answer-item-checkmark {
  position: relative;
  display: inline-block;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  line-height: 1.6em;
  margin: -0.4rem 1rem 0 -3rem;
  text-align: center;
  font-size: 1.4rem;
  vertical-align: middle;
}

.poll .question .question__answers:not(.question__answers--images) .question__answer-item {
  margin: 0 auto;
}

.poll .question__answers.question__answers--images .question__image .question__answer-item-image,
.poll .question__answers.question__answers--videos .question__video-item .question__video-item {
  padding: 0;
  margin: 0 auto;
}

.poll .question .question__answers.question__answers--images.question__answers--images-2,
.poll .question .question__answers.question__answers--images.question__answers--images-desktop-1,
.poll .question .question__answers.question__answers--images.question__answers--images-desktop-2,
.poll .question .question__answers.question__answers--images.question__answers--images-desktop-3,
.poll .question .question__answers.question__answers--videos.question__answers--images-2,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-1,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-2,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-3 {
  display: inline-block;
  width: auto;
}

.poll .question .question__answers.question__answers--images.question__answers--images-2 .question__image,
.poll .question .question__answers.question__answers--images.question__answers--images-desktop-1 .question__image,
.poll .question .question__answers.question__answers--images.question__answers--images-desktop-2 .question__image,
.poll .question .question__answers.question__answers--images.question__answers--images-desktop-3 .question__image,
.poll .question .question__answers.question__answers--videos.question__answers--images-2 .question__video-item,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-1 .question__video-item,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-2 .question__video-item,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-3 .question__video-item {
  max-width: 100%;
  margin: 1.5rem 0;
  padding: 0 1.5rem;
  float: left;
}

.poll .question .question__answers.question__answers--images.question__answers--images-1 .question__image,
.poll .question .question__answers.question__answers--images.question__answers--images-desktop-1 .question__image,
.poll .question .question__answers.question__answers--videos.question__answers--images-1 .question__video-item,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-1 .question__video-item {
  float: none;
  width: 100%;
}

.poll .question .question__answers.question__answers--images.question__answers--images-1 .question__image,
.poll .question .question__answers.question__answers--videos.question__answers--images-1 .question__video-item {
  width: 100%;
}

.poll .question .question__answers.question__answers--images.question__answers--images-desktop-2 .question__image,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-2 .question__video-item {
  width: 50%;
}

.poll .question .question__answers.question__answers--images.question__answers--images-desktop-3 .question__image,
.poll .question .question__answers.question__answers--videos.question__answers--images-desktop-3 .question__video-item {
  width: 33.33%;
}

.poll .question .question__answers.question__answers--images.question__answers--images-2 .question__image,
.poll .question .question__answers.question__answers--videos.question__answers--images-2 .question__video-item {
  clear: none;
  width: 50%;
}

.poll
  .question
  .question__answers.question__answers--images.question__answers--images-2
  .question__image:nth-child(2n + 1),
.poll
  .question
  .question__answers.question__answers--images.question__answers--images-desktop-2
  .question__image:nth-child(2n + 1),
.poll
  .question
  .question__answers.question__answers--images.question__answers--images-desktop-3
  .question__image:nth-child(3n + 1),
.poll
  .question
  .question__answers.question__answers--videos.question__answers--images-2
  .question__video-item:nth-child(2n + 1),
.poll
  .question
  .question__answers.question__answers--videos.question__answers--images-desktop-2
  .question__video-item:nth-child(2n + 1),
.poll
  .question
  .question__answers.question__answers--videos.question__answers--images-desktop-3
  .question__video-item:nth-child(3n + 1) {
  clear: both;
}

.poll .question__answers .question__answer-item-result {
  display: block;
  position: absolute;
  right: -0.5rem;
  top: 0;
  background-color: rgba(0, 0, 0, 0.65);
  color: #ffffff;
  min-width: 30px;
  height: 30px;
  border-radius: 15px;
  line-height: 30px;
  text-align: center;
  font-size: 10px;
  padding-left: 4px;
  padding-right: 4px;
  z-index: 10;
  transform: translateX(100%);
}

.poll .question__answers .question__answer-item-result.ng-enter {
  transition: opacity 275ms linear;
  opacity: 0;
}

.poll .question__answers .question__answer-item-result.ng-enter-active {
  opacity: 1;
}

.poll .question__answers.question__answers--images .question__answer-item-result {
  right: 0;
  transform: none;
}

.poll ul.question__answers .question__answer-item-result {
  top: -4px;
  right: -10px;
}

.category-mobile .poll ul.question__answers .question__answer-item-result {
  bottom: 0;
  top: auto;
  width: 40px;
  height: 40px;
  line-height: 40px;
  border-radius: 50%;
}

.poll .question__answers.question__answers--images .question__answer-item {
  display: inline-block;
  backface-visibility: hidden;
  vertical-align: bottom;
  cursor: pointer;
  padding: 0;
  margin-bottom: 3rem;
}
.poll .question__answers .question__answer-item.question__image:hover,
.poll .question__answers .question__answer-item > .question__answer-item-wrapper:hover,
.poll .question__answers .question__answer-item.question__answer-item--selected > .question__answer-item-wrapper {
  opacity: 1;
}

.poll .question__answers .question__answer-item.question__answer-item--selected {
  opacity: 1 !important;
}

.poll .question__link {
  font-size: 1.8rem;
  display: block;
  margin-top: 1.5rem;
}
.poll .content__item-action.content__item-action--disabled {
  transform: translate(0, 100%);
  opacity: 0;
}

.poll .content__item-action .content__item-action,
.poll .content__item-action-error {
  transition:
    transform 0.3s cubic-bezier(0.2, 0.1, 0.07, 1),
    opacity 0.2s linear;
}

.poll .content__item-action .content__item-action {
  transform: translate(0, 0%);
  opacity: 1;
  position: relative;
}

.poll .content__item-action-error {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  align-items: center;
  justify-content: center;
}

.poll .content__item-action-error.ng-enter {
  transform: translate(0, -100%);
  opacity: 0;
}

.poll .content__item-action-error.ng-enter-active {
  transform: translate(0, 0%);
  opacity: 1;
}

.poll .content__item-action-error.ng-leave {
  -webkit-transform: translate(0, 0%);
  -moz-transform: translate(0, 0%);
  transform: translate(0, 0%);
  opacity: 1;
}

.poll .content__item-action-error.ng-leave-active {
  transform: translate(0, -100%);
  opacity: 0;
}

.poll.poll--bullets-letter .question__answers .question__answer-item-wrapper,
.poll.poll--bullets-decimal .question__answers .question__answer-item-wrapper {
  vertical-align: top;
}

.poll.poll--bullets-decimal .question__answers {
  counter-reset: num;
}

.poll.poll--bullets-decimal .question__answers .question__answer-item {
  counter-increment: num;
}

.poll.poll--bullets-decimal .question__answers .question__answer-item-wrapper:before {
  content: counter(num) '. ';
  line-height: 1.2em;
  vertical-align: top;
}

.poll.poll--bullets-letter .question__answers {
  counter-reset: letter;
}

.poll.poll--bullets-letter .question__answers .question__answer-item {
  counter-increment: letter;
}

.poll.poll--bullets-letter .question__answers .question__answer-item-wrapper:before {
  content: counter(letter, upper-alpha) '. ';
  line-height: 1.2em;
  vertical-align: top;
}

.poll.poll--bullets-disc .question__answers .question__answer-item-wrapper:before {
  content: '\f111';
  display: inline-block;
  font-family: FontAwesome;
  font-weight: normal;
  font-style: normal;
  vertical-align: middle;
  margin-right: 1rem;
  margin-left: 2px;
  font-size: 1.4rem;
}

/* Custom, iPhone Retina */
@media only screen and (max-width: 480px), screen and (max-height: 600px) {
  .site--mobile .poll .question .question__content .question__image {
    max-width: 80%;
  }

  .site--mobile .poll .question .question__wrapper {
    font-size: 2.2rem;
  }

  .site--mobile .poll .question__answers {
    font-size: 1.8rem;
    line-height: 2.2rem;
  }

  .site--mobile .poll .question__answers .question__answer-item {
    margin-bottom: 1rem;
  }
}

.poll .question__answers--videos {
  font-size: 1.4rem !important;
  margin-left: -1.5rem;
  margin-right: -1.5rem;
}

.poll .question__answers--videos .question__answer-item {
  padding: 1.5rem;
}

.poll .question__answers--videos .question__answer-item-wrap {
  display: block;
  text-align: center;
}

.poll .question__answers--videos .question__answer-item-outer {
  display: inline-block;
  line-height: 0;
}

.site--mobile .poll .question__answers--videos .question__answer-item-outer {
  display: block;
  line-height: 0;
  margin-bottom: 30px;
  .content__item--action {
    margin-top: 30px;
    margin-bottom: 30px;
  }
}

.poll .question__answers--videos .question__answer-item-inner {
  display: inline-block;
  width: auto;
  position: relative;
  cursor: pointer;
  font-size: 0;
  letter-spacing: 0;
  line-height: 0;
}

.poll .question__answers--videos .question__answer-item-image {
  margin: 0 !important;
}

.poll .question__answer-item-play {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.65);
  opacity: 0;
  transition: opacity 175ms linear;
}

.poll .question__answer-item-play:before {
  content: '';
  display: block;
  width: 6rem;
  height: 6rem;
  background-color: #525252;
  border-radius: 50%;
  position: absolute;
  left: 50%;
  top: 50%;
  margin: -3rem 0 0 -3rem;
  z-index: 4;
  opacity: 0.9;
  cursor: pointer;
  transform: scale(0.85);
  transition: -webkit-transform 175ms ease-out;
  transition: transform 175ms ease-out;
}

.poll .question__answer-item-play:after {
  content: '';
  display: block;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 1.5rem 0 1.5rem 2.5rem;
  border-color: transparent transparent transparent #fff;
  position: absolute;
  left: 50%;
  top: 50%;
  margin: -1.5rem 0 0 -1rem;
  z-index: 5;
  cursor: pointer;
  transform: scale(0.85);
  transition: transform 175ms ease-out;
}

.poll .question__answer-item-play:hover {
  opacity: 1;
}

.poll .question__answer-item-play:hover::before,
.poll .question__answer-item-play:hover::after {
  transform: scale(1);
}

.poll .question__answers--videos .question__answer-item-text {
  display: block;
  width: 100%;
  text-align: left;
  padding: 1rem;
  line-height: 1.4;
}

.poll-modal__dialog {
  width: 90%;
  max-width: 40rem;
  margin: 5.6rem auto;
}

.poll-modal__content {
  font-size: 1.4rem;
  border-radius: 0.35rem 0.35rem 0.4rem 0.4rem;
  text-align: center;
  box-shadow: 0 0 2rem rgba(0, 0, 0, 0.2);
  border: 0 none;
}

.poll-modal__header {
  padding: 0;
  border-bottom: 0 none;
}

.poll-modal__body {
  padding: 3em 1em;
  margin-bottom: 1.5rem;
  color: #000;
  font-size: 1.6rem;
  line-height: 1.4;
}

.poll-modal__footer {
  padding: 0;
  border-top: 0 none;
}

.poll-modal__action {
  width: 50%;
  height: 6rem;
  line-height: 6rem;
  text-transform: uppercase;
  font-size: 1.4rem;
  color: #fff;
  transition: background-color 0.2s;
  cursor: pointer;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 0;
}

.poll-modal__action--yes {
  background: #fc7169;
  border-radius: 0 0 0 0.35rem;
}

.poll-modal__action--no {
  background: #b6bece;
  border-radius: 0 0 0.35rem 0;
}

.poll-modal .poll-modal__close:hover,
.poll-modal .poll-modal__close {
  background-color: transparent !important;
  color: #8f9cb5;
  position: absolute;
  top: 0.6rem;
  right: 0.8rem;
  width: 3rem;
  height: 3rem;
  padding: 0;
  margin: 0;
  font-size: 2.6rem;
  opacity: 1;
  z-index: 1;
  cursor: pointer;
}

@media only screen and (min-width: 1170px) {
  .poll-modal__dialog {
    margin: 11.2rem auto;
  }
}

@media only screen and (max-width: 736px) {
  .poll-modal__body {
    font-size: 1.4rem;
  }
}
</style>
